import ReceiptPrinterEncoder from '@point-of-sale/receipt-printer-encoder';
import apcsLogo from '../assets/images/apc-logo.png';

/**
 * Load the APCS logo as an ImageData object for thermal printing.
 * Scales it down to fit a 58mm printer (~384px wide).
 * We target ~200px wide for a centered logo with some margin.
 */
const loadLogoImageData = () => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            // Scale to fit 58mm printer width. Target ~200px wide.
            const targetWidth = 200;
            const aspectRatio = img.height / img.width;
            const targetHeight = Math.round(targetWidth * aspectRatio);

            // Width and height must be multiples of 8 for ESC/POS
            const width = Math.ceil(targetWidth / 8) * 8;
            const height = Math.ceil(targetHeight / 8) * 8;

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            // Fill white background first (thermal printers treat dark pixels as printed)
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);

            // Draw the logo centered on the canvas
            const offsetX = (width - targetWidth) / 2;
            const offsetY = (height - targetHeight) / 2;
            ctx.drawImage(img, offsetX, offsetY, targetWidth, targetHeight);

            const imageData = ctx.getImageData(0, 0, width, height);
            resolve({ imageData, width, height });
        };
        img.onerror = (err) => {
            console.warn('Failed to load APCS logo for printing, skipping logo.', err);
            resolve(null); // Don't block printing if logo fails
        };
        img.src = apcsLogo;
    });
};

export const connectAndPrintUSB = async (dataToPrint) => {
    try {
        // Request USB device from the user.
        // We leave filters empty so the user can select their specific printer.
        const device = await navigator.usb.requestDevice({ filters: [] });

        await device.open();

        // Select configuration if not already selected
        if (device.configuration === null) {
            await device.selectConfiguration(1);
        }

        // Claim the first interface
        await device.claimInterface(0);

        // Pre-load the logo
        const logoData = await loadLogoImageData();

        // Initialize the encoder for standard ESC/POS
        const encoder = new ReceiptPrinterEncoder({
            language: 'esc-pos',
        });

        // Start building the binary data
        encoder.initialize();

        dataToPrint.forEach((registrant, index) => {
            const performers = registrant.performers || [];
            let nameToPrint = '', address = '', city = '', phone = '', zipCode = '';

            if (performers.length > 0) {
                nameToPrint = performers.map(p => p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim()).join(' & ');
                city = String(performers[0].city || '');
                address = String(performers[0].addressLine || '');
                zipCode = String(performers[0].zipCode || '');
                phone = String(performers[0].phoneNumber || '');
            } else {
                nameToPrint = registrant.name || '';
            }

            // Print APCS logo centered at the top of each label
            if (logoData) {
                encoder
                    .align('center')
                    .image(logoData.imageData, logoData.width, logoData.height, 'atkinson')
                    .newline();
            }

            // Print the full shipping details
            encoder
                .align('left')
                .text(`From :APCS Music`)
                .newline()
                .text(`Phone Number: (+62)82213002686`)
                .newline()
                .newline()
                .text(`To: ${nameToPrint}`)
                .newline()
                .text(`Phone: ${phone}`)
                .newline()
                .text(`Addr: ${address}`)
                .newline()
                .text(`City: ${city}`)
                .newline()
                .text(`Zip: ${zipCode}`)
                .newline();

            // Add a separator between labels if printing multiple
            if (index < dataToPrint.length - 1) {
                encoder
                    .newline()
                    .text('--------------------------------')
                    .newline()
                    .newline();
            }
        });

        // Feed some lines before cutting (if printer supports cut)
        encoder.newline().newline().newline();

        const result = encoder.encode();

        // Find the "OUT" endpoint to send data to
        let outEndpoint;
        for (const endpoint of device.configuration.interfaces[0].alternate.endpoints) {
            if (endpoint.direction === "out") {
                outEndpoint = endpoint;
                break;
            }
        }

        if (!outEndpoint) {
            throw new Error("No OUT endpoint found on this USB device. It might not be a printer.");
        }

        // Send the raw binary data to the printer
        await device.transferOut(outEndpoint.endpointNumber, result);

        // Optionally close the device after printing
        await device.close();

        return { success: true };
    } catch (error) {
        console.error('USB Print Error:', error);
        return { success: false, error: error.message };
    }
};
